import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { statusColor, formatDate, formatDollars, cn } from "@/lib/utils";
import {
    Plus,
    Search,
    Send,
    FileEdit,
    DollarSign,
    CheckCircle,
    Clock,
    ArrowRight,
    RefreshCw,
    Briefcase,
    CalendarPlus,
    ExternalLink,
    X,
    ThumbsDown,
} from "lucide-react";
import { toast } from "sonner";
import { SendCustomerDialog } from "@/components/send-customer-dialog";
import { ConversionReviewDialog } from "./conversion-review-dialog";
import { commercialDocStatusLabel } from "@/lib/vocabulary";
import { moneySortValue } from "@/lib/table-sort";
import { usePersistedSort } from "@/lib/use-persisted-sort";
import { conversionToast, type ConversionScope } from "@/lib/estimate-conversion";

/** Mirrors DECLINE_REASON_LABEL in src/server/services/estimateDecline.ts. */
const DECLINE_REASON_LABELS: Record<string, string> = {
    price: "Price is too high",
    other_provider: "Going with someone else",
    not_now: "Not right now",
    other: "Something else",
};
function declineReasonLabel(code: string | null | undefined): string | null {
    if (!code) return null;
    return DECLINE_REASON_LABELS[code] ?? code.replace(/_/g, " ");
}

interface DocRow {
    id: string;
    doc_type: "estimate" | "invoice";
    doc_number: string;
    customer_name: string;
    status: string;
    total_cents: number;
    balance_cents?: number;
    issued_at: string;
    created_at: string;
    declined_at?: string | null;
    decline_reason_code?: string | null;
}

type CommercialTab =
    | "outstanding"
    | "pending_estimates"
    | "approved_ready"
    | "drafts"
    | "accepted"
    | "declined"
    | "paid"
    | "all";

const TABS: { key: CommercialTab; label: string; icon: typeof Send; statusFilter: string[]; docType?: string }[] = [
    {
        key: "outstanding",
        label: "Outstanding",
        icon: DollarSign,
        statusFilter: ["open", "partially_paid"],
        docType: "invoice",
    },
    { key: "pending_estimates", label: "Pending Estimates", icon: Clock, statusFilter: ["sent"], docType: "estimate" },
    // §6b: approvals from the magic-link flow land here — one row per
    // approved estimate not yet turned into a job. Default landing tab when
    // arriving from the Command Center "Approved Estimates" card.
    { key: "approved_ready", label: "Approved — ready to schedule", icon: CalendarPlus, statusFilter: [] },
    { key: "drafts", label: "Drafts", icon: FileEdit, statusFilter: ["draft"] },
    { key: "accepted", label: "Accepted", icon: CheckCircle, statusFilter: ["accepted"], docType: "estimate" },
    // A declined estimate used to be bucketed with paid + cancelled under
    // "Paid / Closed" — a lost sale filed under "Paid", which is why nobody
    // ever worked them. It gets its own tab (#1124 follow-up).
    { key: "declined", label: "Declined", icon: ThumbsDown, statusFilter: ["declined"], docType: "estimate" },
    { key: "paid", label: "Paid / Closed", icon: CheckCircle, statusFilter: ["paid", "cancelled"] },
    { key: "all", label: "All", icon: Briefcase, statusFilter: [] },
];

interface ApprovedReadyRow {
    id: string;
    title: string | null;
    display_number: string | null;
    subtotal: string;
    approved_by_name: string | null;
    approved_via_token_at: string | null;
    updated_at: string;
    days_since_approved: number;
    customer_id: string;
    company_name: string;
    site_id: string | null;
    site_address: string | null;
    site_name: string | null;
    // Prompt 22 conversion queue enrichment
    detected_serials: string[];
    match_status: "clean" | "needs_review" | "no_serials";
    matched_device_count: number;
    wo_match: { itemId: string; woNumber: string | null; reason: string } | null;
}

const PAGE_SIZE = 25;

/** Must match COMMERCIAL_DOCUMENTS_SORT on the server (pagedListSorts.ts). */
const DOC_SORT_KEYS = [
    "doc_number",
    "doc_type",
    "customer_name",
    "status",
    "total_cents",
    "balance_cents",
    "created_at",
] as const;

export default function CommercialPage() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get("page")) || 1;
    const search = searchParams.get("q") ?? "";
    const tabParam = searchParams.get("tab") ?? "outstanding";
    const activeTab = TABS.find((t) => t.key === tabParam) ? (tabParam as CommercialTab) : "outstanding";
    // Location Page V2: the site page's unpaid-invoices chip links here with
    // ?siteId= so the list opens pre-filtered to that location's documents.
    const siteIdFilter = searchParams.get("siteId") ?? "";
    const [searchInput, setSearchInput] = useState(search);

    const tabDef = TABS.find((t) => t.key === activeTab)!;

    // Sorted on the server — the list is paged, so a browser sort would only
    // reorder the 25 rows on screen (faster paths 7h). Remembered per user.
    const sort = usePersistedSort({
        tableId: "commercial.documents",
        defaultKey: "created_at",
        defaultDir: "desc",
        validKeys: DOC_SORT_KEYS,
    });

    const { data, isLoading } = useQuery({
        queryKey: ["commercial", activeTab, page, search, siteIdFilter, sort.sortKey, sort.sortDir],
        queryFn: () => {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                offset: String((page - 1) * PAGE_SIZE),
                sort: sort.sortKey ?? "created_at",
                dir: sort.sortDir,
            });
            if (search) params.set("q", search);
            if (siteIdFilter) params.set("siteId", siteIdFilter);
            for (const s of tabDef.statusFilter) {
                params.append("status", s);
            }
            if (tabDef.docType) params.set("doc_type", tabDef.docType);
            return api.get<{ rows: DocRow[]; total: number }>(`/api/commercial/documents?${params}`);
        },
        // Wait for the saved sort so the list doesn't load twice.
        enabled: activeTab !== "approved_ready" && sort.loaded,
    });

    // Resolve a friendly name for the site-filter chip (warm cache from the
    // site page / breadcrumbs; falls back to its own fetch on direct load).
    const siteFilterQ = useQuery({
        queryKey: ["site-detail", siteIdFilter],
        queryFn: () =>
            api.get<{ site: { name: string | null; address_line: string | null } }>(`/api/sites/${siteIdFilter}`),
        enabled: !!siteIdFilter,
        staleTime: 60_000,
        retry: false,
    });
    const siteFilterLabel =
        siteFilterQ.data?.site?.name?.trim() ||
        siteFilterQ.data?.site?.address_line?.trim() ||
        (siteIdFilter ? `#${siteIdFilter.slice(0, 8)}` : "");

    // Prompt 22: the ready-to-schedule tab is a working line — the
    // conversion-queue feed adds detected serials, match confidence and the
    // WO auto-attach signal per row.
    const approvedReady = useQuery({
        queryKey: ["commercial", "conversion-queue", siteIdFilter],
        queryFn: () =>
            api.get<{ estimates: ApprovedReadyRow[]; remaining: number }>(
                siteIdFilter
                    ? `/api/estimates/conversion-queue?siteId=${siteIdFilter}`
                    : "/api/estimates/conversion-queue",
            ),
        enabled: activeTab === "approved_ready",
    });

    const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

    const setTab = (key: CommercialTab) => {
        setSearchParams((p) => {
            p.set("tab", key);
            p.set("page", "1");
            return p;
        });
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setSearchParams((p) => {
            p.set("q", searchInput);
            p.set("page", "1");
            return p;
        });
    };

    // The "Remind" action opens the compose/preview dialog (recipients +
    // editable subject/body) instead of firing an instant send.
    const [sendRow, setSendRow] = useState<DocRow | null>(null);

    const convertToJob = useMutation({
        mutationFn: (estimateId: string) =>
            api.post<ConversionScope & { job?: { id: string } }>(`/api/estimates/${estimateId}/create-followup-job`),
        onSuccess: (data) => {
            const t = conversionToast(data);
            if (t.warn) toast.warning(t.text, { duration: 12000 });
            else toast.success(t.text);
            qc.invalidateQueries({ queryKey: ["commercial"] });
            if (data?.job?.id) navigate(`/jobs/${data.job.id}`);
        },
        onError: () => toast.error("Failed to create job"),
    });

    // ── Prompt 22: conversion queue machinery ────────────────────────────
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [reviewEstimateId, setReviewEstimateId] = useState<string | null>(null);
    const [dismissRow, setDismissRow] = useState<ApprovedReadyRow | null>(null);
    const [dismissReason, setDismissReason] = useState("");

    interface ConvertResponse extends ConversionScope {
        job: { id: string };
        devicesLinked: number;
        bypassesIncluded: number;
        woLinked: boolean;
        needsDevices: boolean;
    }

    const convertOne = useMutation({
        mutationFn: (estimateId: string) =>
            api.post<ConvertResponse>(`/api/estimates/${estimateId}/convert-to-job`, {}),
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ["commercial"] });
            const t = conversionToast(data);
            if (t.warn) toast.warning(t.text, { duration: 12000 });
            else toast.success(t.text);
            navigate(`/jobs/${data.job.id}`);
        },
        onError: (e, estimateId) => {
            const body = e instanceof ApiError ? (e.body as { error?: string; message?: string }) : undefined;
            if (body?.error === "NEEDS_REVIEW") {
                // No silent attachment, ever — drop into the match review.
                setReviewEstimateId(estimateId);
                return;
            }
            toast.error(body?.message ?? "Couldn't create the job");
        },
    });

    // Bulk: clean rows create in one sweep; anything that trips a review
    // requirement stays queued and is reported.
    const bulkCreate = useMutation({
        mutationFn: async (ids: string[]) => {
            let created = 0;
            let unmatchedLines = 0;
            const queued: string[] = [];
            const failed: string[] = [];
            for (const id of ids) {
                try {
                    const r = await api.post<ConvertResponse>(`/api/estimates/${id}/convert-to-job`, {});
                    created += 1;
                    unmatchedLines += r?.unmatchedLines?.length ?? 0;
                } catch (e) {
                    const body = e instanceof ApiError ? (e.body as { error?: string }) : undefined;
                    if (body?.error === "NEEDS_REVIEW") queued.push(id);
                    else failed.push(id);
                }
            }
            return { created, queued, failed, unmatchedLines };
        },
        onSuccess: ({ created, queued, failed, unmatchedLines }) => {
            qc.invalidateQueries({ queryKey: ["commercial"] });
            setSelectedIds(new Set());
            const bits: string[] = [];
            if (created > 0) bits.push(`${created} job${created === 1 ? "" : "s"} created`);
            if (queued.length > 0) bits.push(`${queued.length} need${queued.length === 1 ? "s" : ""} match review`);
            if (failed.length > 0) bits.push(`${failed.length} failed`);
            // #1611: lines naming a device the site doesn't have — said, not hidden.
            if (unmatchedLines > 0)
                bits.push(
                    `${unmatchedLines} line${unmatchedLines === 1 ? "" : "s"} didn't match a device — pick or create on the jobs`,
                );
            const msg = bits.join("; ") || "Nothing to create";
            if (failed.length > 0) toast.error(msg);
            else if (unmatchedLines > 0) toast.warning(msg, { duration: 12000 });
            else toast.success(msg);
        },
    });

    const dismissM = useMutation({
        mutationFn: ({ estimateId, reason }: { estimateId: string; reason: string }) =>
            api.post(`/api/estimates/${estimateId}/dismiss-conversion`, { reason }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["commercial"] });
            toast.success("Estimate dismissed — no job needed.");
            setDismissRow(null);
            setDismissReason("");
        },
        onError: (e) => {
            const body = e instanceof ApiError ? (e.body as { message?: string }) : undefined;
            toast.error(body?.message ?? "Couldn't dismiss the estimate");
        },
    });

    const columns: Column<DocRow>[] = [
        {
            key: "doc_number",
            header: "Number",
            sortable: true,
            render: (r) => (
                <span className="font-mono text-xs font-medium">{r.doc_number || `#${r.id.slice(0, 8)}`}</span>
            ),
        },
        {
            key: "doc_type",
            header: "Type",
            sortable: true,
            className: "w-28",
            render: (r) => (
                <Badge variant="outline" className="capitalize">
                    {r.doc_type}
                </Badge>
            ),
        },
        { key: "customer_name", header: "Customer", sortable: true },
        {
            key: "status",
            header: "Status",
            sortable: true,
            className: "w-28",
            render: (r) => (
                <div className="flex flex-col gap-0.5">
                    <Badge className={statusColor(r.status)} variant="outline">
                        {commercialDocStatusLabel(r.status)}
                    </Badge>
                    {r.status === "declined" && declineReasonLabel(r.decline_reason_code) && (
                        <span className="text-[11px] text-foreground/70">
                            {declineReasonLabel(r.decline_reason_code)}
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: "total_cents",
            header: "Total",
            sortable: true,
            firstSortDir: "desc",
            className: "w-28 text-right",
            render: (r) => (
                <span className="font-medium">{r.total_cents != null ? formatDollars(r.total_cents / 100) : "—"}</span>
            ),
        },
        // H17: surface what's still owed so partially-paid invoices read as
        // "balance due", not their original total. Only meaningful on the
        // invoice-centric tabs.
        ...((activeTab === "outstanding" || activeTab === "all"
            ? [
                  {
                      key: "balance_cents" as keyof DocRow,
                      header: "Balance Due",
                      sortable: true,
                      firstSortDir: "desc" as const,
                      className: "w-28 text-right",
                      render: (r: DocRow) =>
                          r.doc_type === "invoice" && r.balance_cents != null ? (
                              <span
                                  className={cn(
                                      "font-semibold",
                                      r.balance_cents > 0 ? "text-amber-700 dark:text-amber-300" : "text-foreground/60",
                                  )}
                              >
                                  {formatDollars(r.balance_cents / 100)}
                              </span>
                          ) : (
                              <span className="text-muted-foreground">—</span>
                          ),
                  },
              ]
            : []) as Column<DocRow>[]),
        {
            key: "created_at",
            header: "Date",
            sortable: true,
            firstSortDir: "desc",
            className: "w-32",
            render: (r) => (
                <span className="text-foreground/80 text-xs">{formatDate(r.issued_at || r.created_at)}</span>
            ),
        },
        {
            key: "id" as keyof DocRow,
            header: "",
            className: "w-36",
            render: (r) => (
                <div className="flex items-center gap-1">
                    {(activeTab === "outstanding" || activeTab === "pending_estimates") && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSendRow(r);
                            }}
                        >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Remind
                        </Button>
                    )}
                    {activeTab === "accepted" && r.doc_type === "estimate" && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-green-700"
                            onClick={(e) => {
                                e.stopPropagation();
                                convertToJob.mutate(r.id);
                            }}
                        >
                            <ArrowRight className="h-3 w-3 mr-1" />
                            Create Job
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    // §6b + Prompt 22: the working line. Age chip goes amber past 3 days
    // (approved work going stale is lost revenue); serial/WO/match chips
    // show conversion confidence up front.
    const queueRows = approvedReady.data?.estimates ?? [];
    const bulkEligible = (r: ApprovedReadyRow) => r.match_status !== "needs_review";
    const toggleSelected = (id: string) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const approvedReadyColumns: Column<ApprovedReadyRow>[] = [
        {
            key: "id" as keyof ApprovedReadyRow,
            header: "",
            className: "w-8",
            render: (r) =>
                bulkEligible(r) ? (
                    <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelected(r.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select estimate ${r.display_number ?? r.id}`}
                    />
                ) : (
                    <span title="Needs match review before it can bulk-create" className="text-amber-500">
                        •
                    </span>
                ),
        },
        {
            key: "company_name",
            header: "Customer",
            sortable: true,
            render: (r) => (
                <button
                    type="button"
                    className="font-medium text-primary hover:underline text-left"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/customers/${r.customer_id}`);
                    }}
                >
                    {r.company_name}
                </button>
            ),
        },
        {
            key: "site_address",
            header: "Site",
            sortable: true,
            sortAccessor: (r) => r.site_name || r.site_address,
            render: (r) => <span className="text-sm">{r.site_name || r.site_address || "—"}</span>,
        },
        {
            key: "title",
            header: "Estimate",
            sortable: true,
            sortAccessor: (r) => r.display_number || r.id,
            render: (r) => (
                <span className="text-sm">
                    <span className="font-mono text-xs font-medium">{r.display_number || `#${r.id.slice(0, 8)}`}</span>
                    {r.title ? <span className="text-foreground/70"> — {r.title}</span> : null}
                </span>
            ),
        },
        {
            key: "subtotal",
            header: "Amount",
            className: "w-28 text-right",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => moneySortValue(r.subtotal),
            render: (r) => <span className="font-medium">{formatDollars(Number(r.subtotal))}</span>,
        },
        {
            key: "approved_by_name",
            header: "Approved by",
            sortable: true,
            sortAccessor: (r) => r.approved_by_name || "Customer",
            render: (r) => (
                <span className="text-sm">
                    {r.approved_by_name || <span className="text-muted-foreground">Customer</span>}
                    <span className="block text-xs text-foreground/60">
                        {formatDate(r.approved_via_token_at || r.updated_at)}
                    </span>
                </span>
            ),
        },
        {
            key: "detected_serials" as keyof ApprovedReadyRow,
            header: "Detected",
            render: (r) => (
                <div className="flex flex-wrap items-center gap-1">
                    {(r.detected_serials ?? []).slice(0, 3).map((s) => (
                        <Badge key={s} variant="outline" className="font-mono text-[10px]">
                            {s}
                        </Badge>
                    ))}
                    {(r.detected_serials ?? []).length > 3 && (
                        <span className="text-[10px] text-foreground/60">+{r.detected_serials.length - 3}</span>
                    )}
                    {r.wo_match && (
                        <Badge
                            variant="outline"
                            className="border-sky-300 bg-sky-50 text-sky-800 text-[10px] dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-200"
                            title={r.wo_match.reason}
                        >
                            WO {r.wo_match.woNumber ?? "match"}
                        </Badge>
                    )}
                    {(r.detected_serials ?? []).length === 0 && !r.wo_match && (
                        <span className="text-xs text-muted-foreground">—</span>
                    )}
                </div>
            ),
        },
        {
            key: "match_status" as keyof ApprovedReadyRow,
            header: "Match",
            sortable: true,
            className: "w-28",
            render: (r) =>
                r.match_status === "clean" ? (
                    <Badge className="bg-green-700 text-white text-[10px]">
                        {r.matched_device_count} device{r.matched_device_count === 1 ? "" : "s"}
                    </Badge>
                ) : r.match_status === "needs_review" ? (
                    <Badge
                        variant="outline"
                        className="border-amber-400 text-amber-700 dark:text-amber-300 text-[10px]"
                    >
                        Needs review
                    </Badge>
                ) : (
                    <Badge variant="outline" className="text-[10px] text-foreground/60">
                        No serials
                    </Badge>
                ),
        },
        {
            key: "days_since_approved",
            header: "Age",
            sortable: true,
            firstSortDir: "desc",
            className: "w-24",
            render: (r) => (
                <Badge
                    variant="outline"
                    className={cn(
                        r.days_since_approved > 3
                            ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                            : "text-foreground/70",
                    )}
                    title={
                        r.days_since_approved > 3
                            ? "Approved work going stale — schedule it before it becomes lost revenue."
                            : undefined
                    }
                >
                    {r.days_since_approved}d
                </Badge>
            ),
        },
        {
            key: "id" as keyof ApprovedReadyRow,
            header: "",
            className: "w-64",
            render: (r) => (
                <div className="flex items-center justify-end gap-1">
                    <Button
                        size="sm"
                        className="h-7 text-xs bg-green-700 hover:bg-green-800 text-white"
                        disabled={convertOne.isPending || bulkCreate.isPending}
                        onClick={(e) => {
                            e.stopPropagation();
                            // 2b: review rows never create silently — the
                            // picker resolves each line first.
                            if (r.match_status === "needs_review") setReviewEstimateId(r.id);
                            else convertOne.mutate(r.id);
                        }}
                    >
                        <CalendarPlus className="h-3 w-3 mr-1" />
                        {r.match_status === "needs_review" ? "Review & create" : "Create job"}
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/commercial/${r.id}?type=estimate`);
                        }}
                    >
                        View
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-foreground/70"
                        title="Dismiss — no job needed (reason required, ledgered)"
                        onClick={(e) => {
                            e.stopPropagation();
                            setDismissRow(r);
                        }}
                    >
                        Dismiss
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        title="Open customer"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customers/${r.customer_id}`);
                        }}
                    >
                        <ExternalLink className="h-3 w-3" />
                    </Button>
                </div>
            ),
        },
    ];

    const emptyMessages: Record<CommercialTab, string> = {
        outstanding: "No outstanding invoices. All invoices have been paid!",
        pending_estimates: "No estimates awaiting customer response.",
        approved_ready:
            "No approved estimates waiting to be scheduled. Approvals from the customer email link land here.",
        drafts: "No drafts. Auto-generated documents from completed jobs will appear here.",
        accepted: "No accepted estimates waiting for job creation.",
        declined: "No declined estimates. When a customer turns one down, it lands here with the reason they gave.",
        paid: "No completed documents yet.",
        all: "No documents found.",
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    {/*
                     * Page title renamed from "Billing" per
                     * PRE-CUTOVER-UX-FEEDBACK.md #31. The section holds
                     * estimates, drafts, and pre-bill documents in
                     * addition to invoices, so a billing-only label
                     * mis-set expectations.
                     */}
                    <h1 className="text-2xl font-black tracking-tight text-shell-foreground">Files &amp; Documents</h1>
                    <p className="text-shell-foreground/80 text-sm mt-1">
                        Estimates, invoices, drafts, and payment tracking.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => navigate("/commercial/st-backfill")}>
                        ST translation backfill
                    </Button>
                    <Button onClick={() => navigate("/commercial/new")}>
                        <Plus className="h-4 w-4 mr-1" />
                        New Estimate / Invoice
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-border/60 overflow-x-auto">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={cn(
                                "flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap",
                                activeTab === t.key
                                    ? "border-primary text-primary"
                                    : "border-transparent text-shell-foreground/80 hover:text-shell-foreground",
                            )}
                        >
                            <Icon className="h-4 w-4" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-sm">
                    <Input
                        placeholder="Search documents..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                    <Button type="submit" variant="outline" size="icon">
                        <Search className="h-4 w-4" />
                    </Button>
                </form>
                {siteIdFilter && (
                    <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                        Site: {siteFilterLabel}
                        <button
                            type="button"
                            aria-label="Clear site filter"
                            className="rounded-full p-0.5 hover:bg-primary/20"
                            onClick={() =>
                                setSearchParams((p) => {
                                    p.delete("siteId");
                                    p.set("page", "1");
                                    return p;
                                })
                            }
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                )}
            </div>

            {activeTab === "approved_ready" ? (
                <div className="space-y-3">
                    {/* Working-line bar: N remaining + bulk create for the selection. */}
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2">
                        <span className="text-sm font-medium">
                            {approvedReady.data?.remaining ?? queueRows.length} remaining
                            {queueRows.some((r) => r.match_status === "needs_review") && (
                                <span className="ml-2 text-xs font-normal text-amber-700 dark:text-amber-300">
                                    {queueRows.filter((r) => r.match_status === "needs_review").length} need match
                                    review
                                </span>
                            )}
                        </span>
                        <div className="flex items-center gap-2">
                            {selectedIds.size > 0 && (
                                <Button
                                    size="sm"
                                    className="h-7 text-xs"
                                    disabled={bulkCreate.isPending}
                                    onClick={() => bulkCreate.mutate([...selectedIds])}
                                >
                                    {bulkCreate.isPending
                                        ? "Creating…"
                                        : `Create ${selectedIds.size} job${selectedIds.size === 1 ? "" : "s"}`}
                                </Button>
                            )}
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => {
                                    const eligible = queueRows.filter(bulkEligible).map((r) => r.id);
                                    setSelectedIds((prev) =>
                                        prev.size === eligible.length ? new Set() : new Set(eligible),
                                    );
                                }}
                            >
                                {selectedIds.size === queueRows.filter(bulkEligible).length && selectedIds.size > 0
                                    ? "Clear selection"
                                    : "Select all clean"}
                            </Button>
                        </div>
                    </div>
                    <DataTable
                        columns={approvedReadyColumns}
                        data={queueRows}
                        keyField="id"
                        sortId="commercial.approved-ready"
                        loading={approvedReady.isLoading}
                        onRowClick={(row) => navigate(`/commercial/${row.id}?type=estimate`)}
                        emptyMessage={emptyMessages.approved_ready}
                    />
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={data?.rows ?? []}
                    keyField="id"
                    loading={isLoading || !sort.loaded}
                    onRowClick={(row) => navigate(`/commercial/${row.id}?type=${row.doc_type}`)}
                    emptyMessage={emptyMessages[activeTab]}
                    controlledSort={{
                        key: sort.sortKey,
                        dir: sort.sortDir,
                        onChange: (key, firstDir) => {
                            sort.toggleSort(key, firstDir);
                            // Page 3 of a different order is nobody's intent.
                            setSearchParams((p) => {
                                p.set("page", "1");
                                return p;
                            });
                        },
                    }}
                    page={page}
                    totalPages={totalPages}
                    onPageChange={(p) =>
                        setSearchParams((params) => {
                            params.set("page", String(p));
                            return params;
                        })
                    }
                />
            )}

            {sendRow && (
                <SendCustomerDialog
                    docType={sendRow.doc_type}
                    id={sendRow.id}
                    customerId={null}
                    open={!!sendRow}
                    onOpenChange={(o) => !o && setSendRow(null)}
                    onSent={() => qc.invalidateQueries({ queryKey: ["commercial"] })}
                />
            )}

            {reviewEstimateId && (
                <ConversionReviewDialog estimateId={reviewEstimateId} onClose={() => setReviewEstimateId(null)} />
            )}

            {dismissRow && (
                <div
                    className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
                    onClick={() => setDismissRow(null)}
                >
                    <div
                        className="w-full max-w-md rounded-lg border bg-card p-4 shadow-xl space-y-3"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-sm font-semibold">
                            Dismiss estimate {dismissRow.display_number ?? ""} — no job needed
                        </h3>
                        <p className="text-xs text-foreground/70">
                            The reason is required and ledgered to the estimate's audit trail. The row leaves the queue
                            everywhere.
                        </p>
                        <Input
                            autoFocus
                            placeholder="Why is no job needed? (e.g. work folded into job #…)"
                            value={dismissReason}
                            onChange={(e) => setDismissReason(e.target.value)}
                        />
                        <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => setDismissRow(null)}>
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                disabled={dismissReason.trim().length < 3 || dismissM.isPending}
                                onClick={() =>
                                    dismissM.mutate({ estimateId: dismissRow.id, reason: dismissReason.trim() })
                                }
                            >
                                {dismissM.isPending ? "Dismissing…" : "Dismiss"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
